import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Dropdown, DropdownItem, Modal } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { updateAuthForm } from "../redux/slice/loginSlice";
import { RootState } from "../redux/Store";
import {
  SignOut,
  GetUserRole,
  FeatureAccess,
  SwitchUserRole,
} from "../services/AuthService";
import { fetcheaderinfo } from "../services/UserRegistrationApi";
import { Search, Filter } from "lucide-react";
import "./Style.css";
import {
  clearSearchQuery,
  updateSearchQuery,
} from "../redux/slice/searchSlice";
import AdvancedSearchPopup from "../modules/Case_Management/Case_Search/components/AdvanceSearch";
import SearchableSingleSelectDropdown from "../common/SearchableDropdown";
import InputGroup from "react-bootstrap/InputGroup";
import Form from "react-bootstrap/Form";
import NotificationIcon from "../modules/main/Notification/NoificationIcon";
import {
  setAllowedFeature,
  setCurrentUserRoleApp,
  setView_Case,
} from "../redux/slice/featureAccessSlice";
import ChangePassword from "../modules/login/ChangePaaword";
import { useFeatureAccess } from "../hooks/useFeatureAccess";

interface HeaderProps {
  userid: string | null;
  username: string | null;
  firstname: string | null;
  lastname: string | null;
}

const Header: React.FC<HeaderProps> = ({
  userid,
  username,
  firstname,
  lastname,
}) => {
  const { filterByAccess } = useFeatureAccess();
  const [isDarkMode, setIsDarkMode] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const loggedInUserid = useSelector((state: RootState) => state.auth.userid);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const deviceId = localStorage.getItem("deviceId");
  const deviceType = localStorage.getItem("deviceType");
  const [roleOptions, setroleOptions] = useState<
    { value: number; label: string }[]
  >([]);
  const [userRole, setUserRole] = useState<number | null>(null);
  const token = localStorage.getItem("token");

  const userFullName =
    firstname || lastname ? `${firstname} ${lastname}` : "--";

  const { isSearchEditable } = useSelector(
    (state: RootState) => state.caseHeader,
  );

  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [data, setsetData] = useState("");
  const [currentUserRole, setCurrentUserRole] = useState("");

  const hasDashboardAccess = filterByAccess([{ label: "Dashboard" }], {
    Dashboard: "Dashboard",
  })?.some((opt) => opt.label === "Dashboard");

  const getUserRoleData = async () => {
    try {
      const userData = await GetUserRole(String(token));

      const roles =
        userData?.result?.map((r: any) => ({
          value: r.roleId,
          label: r.roleDescription,
          userId: r.userId,
        })) || [];
      setroleOptions(roles);
      const userCredentialData = await fetcheaderinfo(loggedInUserid);
      setUserRole(userCredentialData.currentAppRoleID);
      setsetData(userCredentialData.emailid);
      setCurrentUserRole(userCredentialData?.currentAppRoleDesc || "");
      dispatch(setCurrentUserRoleApp(userCredentialData?.currentAppRoleDesc || ""));
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    getUserRoleData();
  }, []);

  useEffect(() => {
    if (!location.pathname.includes("/search-case")) {
      if (inputRef.current) {
        inputRef.current.value = "";
      }
      setSearchQuery("");
      dispatch(clearSearchQuery());
    }
  }, [location.pathname, dispatch]);

  const getGreeting = () => {
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const userTime = new Date().toLocaleString("en-US", {
      timeZone: userTimeZone,
    });
    const currentHour = new Date(userTime).getHours();

    if (currentHour < 12) return "Good Morning";
    if (currentHour < 18) return "Good Afternoon";
    return "Good Evening";
  };

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
    document.body.classList.toggle("dark-theme", !isDarkMode);
  };

  const handleRoleChange = async (roleId: number) => {
    const data = {
      roleId: roleId,
      userId: loggedInUserid,
    };
    await SwitchUserRole(data);

    const userCredentialData = await fetcheaderinfo(loggedInUserid);
    localStorage.setItem("userrole", userCredentialData.currentAppRoleDesc);
    window.location.reload();
  };

  const handleLogout = async () => {
    localStorage.removeItem("token");
    sessionStorage.clear();
    dispatch(setView_Case(false));
    dispatch(setAllowedFeature(new Set()));
    dispatch(setCurrentUserRoleApp(""));
    dispatch(updateAuthForm({ token: null, isSuccessful: false }));

    const response = await SignOut({
      Userid: Number(loggedInUserid),
      loginType: "LogOut",
      deviceId: String(deviceId),
      deviceName: String(deviceType),
    });

    if (response.isSuccess) {
      navigate("/", { replace: true });
    }
  };

  const debounce = (func: Function, delay: number) => {
    let timer: NodeJS.Timeout;
    return (...args: any[]) => {
      clearTimeout(timer);
      timer = setTimeout(() => func(...args), delay);
    };
  };

  const handleInputChange = debounce((value: string) => {
    setSearchQuery(value);
    localStorage.removeItem("advancedSearchForm");
    dispatch(updateSearchQuery(value));
    navigate("/search-case", { state: { resetPage: true } });
  }, 300);

  const OpenAdvanceserachPopup = () => {
    if (inputRef.current) {
      inputRef.current.value = "";
    }
    setSearchQuery("");
    dispatch(clearSearchQuery());
    setIsPopupOpen(true);
  };

  const CloseAdvanceserachPopup = () => {
    setIsPopupOpen(false);
  };

  return (
    <header className="d-flex height50">
      <div className="toplogo me-auto">
        <img src="/logo.png" alt="Logo" />
      </div>

      {!isSearchEditable &&
        (hasDashboardAccess ||
          location.pathname === "/search-case" ||
          location.pathname === "/advancesearch-case") &&
        !(
          location.pathname === "/map/associated_locations" ||
          location.pathname === "/appointment/map" ||
          location.pathname === "/mytodos-view-invoice"
        ) && (
          <InputGroup className="mb-1 w-50 mt-1" style={{ left: "-98px" }}>
            <InputGroup.Text id="basic-addon1" className="px-2">
              <span className="material-symbols-outlined text-black-50">
                search
              </span>
            </InputGroup.Text>
            <Form.Control
              ref={inputRef}
              aria-label="Username"
              aria-describedby="basic-addon1"
              onChange={(e) => {
                handleInputChange(e.target.value.trim());
              }}
              onPaste={(e) => {
                e.preventDefault();
                const text = e.clipboardData.getData("text").trim();
                handleInputChange(text);
                if (inputRef.current) {
                  inputRef.current.value = text; // Update input value manually
                }
              }}
            />

            <Filter
              onClick={OpenAdvanceserachPopup}
              className="w-5 h-5 text-gray-500 ml-2 cursor-pointer filtericon"
            />
          </InputGroup>
        )}
      {!isSearchEditable && isPopupOpen && (
        <AdvancedSearchPopup
          isOpen={isPopupOpen}
          onClose={CloseAdvanceserachPopup}
          selectedTab="Case info"
        />
      )}

      <div
        className="d-flex align-items-center"
        style={{
          position: "relative",
          left: "-20px",
          top: "5px",
          cursor: "pointer",
        }}
      >
        <NotificationIcon />
      </div>
      <Dropdown className="flt-right px-3 user_dropdown_header">
        <Dropdown.Toggle
          variant="light"
          id="dropdown-profile"
          className="p-0 border-0 bg-transparent d-flex align-items-center"
          style={{ boxShadow: "none" }}
        >
          <div className="topuserinitial">
            {firstname
              ? lastname
                ? (firstname[0] + lastname[0]).toUpperCase()
                : firstname[0].toUpperCase()
              : "--"}
          </div>
          <div className="d-flex flex-column align-items-start mt-2">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                fontSize: "12px",
                marginBottom: "-5px",
              }}
            >
            </div>
            <div className="col-md-12">
              <div className="row">
                <div className="col-md-8">
                  <span className="userName_full d-block wish userEllipseses" title={userFullName}>
                    {userFullName}
                  </span>
                   <span className="userEllipseses" title={currentUserRole}>{currentUserRole}</span>
                </div>
                <div className="col-md-4">
                  <span className="material-icons material-symbols-outlined arrow_down" style={{marginTop:"22px"}}>
                    keyboard_arrow_down
                  </span>
                </div>
              </div>
            </div>
          </div>
        </Dropdown.Toggle>

        <Dropdown.Menu>
          <Dropdown.Item
  className="accountDetailLis"
  onClick={(e) => {
    e.preventDefault();
    window.location.href = `mailto:${data?.trim()}`;
  }}
>
  {data}
</Dropdown.Item>
          <Dropdown.Item onClick={() => setShowChangePasswordModal(true)}>
            <span className="material-symbols-outlined icon_label_header">
              lock_reset{" "}
            </span>{" "}
            <span>Change Password</span>
          </Dropdown.Item>
          {/* <Dropdown.Item href="#settings" className="d-flex align-items-center">
            <span style={{ marginRight: "auto" }}>
              {" "}
              <span className="material-symbols-outlined icon_label_header">
                format_color_fill{" "}
              </span>{" "}
              <span>Theme</span>
            </span>
            <div
              style={{
                width: "40px",
                height: "20px",
                borderRadius: "10px",
                backgroundColor: isDarkMode ? "#007BFF" : "#ccc",
                display: "flex",
                alignItems: "center",
                justifyContent: isDarkMode ? "flex-end" : "flex-start",
                cursor: "pointer",
                padding: "2px",
              }}
              onClick={toggleTheme}
              role="button"
              aria-label="Toggle Theme"
            >
              <div
                style={{
                  width: "16px",
                  height: "16px",
                  borderRadius: "50%",
                  backgroundColor: isDarkMode ? "#fff" : "#000",
                }}
              ></div>
            </div>
          </Dropdown.Item> */}
          <Dropdown.Item as="div" className="px-3 py-2 user-role-section">
            <a className="fw-semibold mb-2 text-muted text-decoration-none">
              {" "}
              <span className="material-symbols-outlined icon_label_header">
                manage_accounts{" "}
              </span>{" "}
              <span>User Role</span>
            </a>
            <div className="firstRowDro">
              {roleOptions.map((role) => (
                <div
                  key={role.value}
                  className="d-flex align-items-center py-1 role-option "
                  style={{ cursor: "pointer" }}
                  onClick={() => handleRoleChange(role.value)}
                >
                  <Form.Check
                    type="radio"
                    className="me-1"
                    checked={userRole === role.value}
                    readOnly
                  />
                  <a className="role-link">{role.label}</a>
                </div>
              ))}
            </div>
          </Dropdown.Item>

          <Dropdown.Item onClick={handleLogout}>
            {" "}
            <span className="material-symbols-outlined icon_label_header">
              logout{" "}
            </span>{" "}
            <span>Logout</span>
          </Dropdown.Item>
        </Dropdown.Menu>
        <Modal
          show={showChangePasswordModal}
          onHide={() => setShowChangePasswordModal(false)}
          size="sm"
          centered
          aria-labelledby="contained-modal-title-vcenter"
          className="recordList changePasswordModal"
        >
          <Modal.Header closeButton className="d-flex">
            <Modal.Title className="fw-bold text-left modelheading offcanvas-title ">
              Change Password
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <ChangePassword />
          </Modal.Body>
        </Modal>
      </Dropdown>
    </header>
  );
};

export default Header;
